<template>
    <v-card v-if="freeShippingThreshold > 0 && offertProducts.length > 0" width="100%" flat>
        <v-card-text class="primary-transparent-5 title">
            <v-layout row>
                <v-flex v-if="$vuetify.breakpoint.mdAndUp">
                    <span class="primary--text text-uppercase">
                        {{ 'Free shipping from' | translation }}
                        {{ getFreeShippingMinimalPrice(freeShippingThreshold) | currency }}.
                    </span>

                    <span class="font-weight-regular">
                        {{ 'Fill your shopping cart' | translation }}
                        <span class="font-weight-bold">
                            {{ 'with only' | translation }}
                            {{ freeShippingThreshold | currency }}
                        </span>
                    </span>

                    <v-avatar size="30" tile>
                        <v-img :src="require('./assets/NoPath.png')" />
                    </v-avatar>
                </v-flex>

                <v-flex v-else>
                    <v-layout row class="subheading pa-2 align-center">
                        <v-flex xs10>
                            <v-layout row>
                                <div class="primary--text text-uppercase font-weight-bold">
                                    {{ 'Freee shipping from' | translation }}
                                    {{ getFreeShippingMinimalPrice(freeShippingThreshold) | currency }}.
                                </div>
                            </v-layout>

                            <v-layout row>
                                <div>
                                    {{ 'Fill your shopping cart with only ' | translation }}
                                    {{ freeShippingThreshold | currency }}
                                </div>
                            </v-layout>
                        </v-flex>

                        <v-flex xs2 class="pa-0">
                            <v-avatar size="50" tile>
                                <v-img :src="require('./assets/NoPath@2x.png')" />
                            </v-avatar>
                        </v-flex>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-card-text>

        <v-layout row wrap class="px-2">
            <template v-for="(offertProduct, index) in offertProducts">
                <v-flex xs12 :class="'md' + 12 / cols">
                    <related-products-thumbnail
                        v-if="offertProduct.isNotBuyable[offertProduct.id] === false"
                        class="px-2 py-3"
                        :product="offertProduct"
                        :key="offertProduct.id"
                    />
                </v-flex>

                <v-flex xs12 v-if="(index + 1) % cols === 0" :key="`related-product-divider-${offertProduct.id}`">
                    <v-divider/>
                </v-flex>
            </template>
        </v-layout>
    </v-card>
</template>

<style scoped>
.shipping-info-message {
    transform: rotate(-45deg);
}
</style>

<script>
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import { getProducts } from '@breakmedia/prime-vue-product/src/store/getProducts';
import { Observable } from 'rxjs';

export default {
    name: 'FreeShippingInfo',
    data() {
        return {
            offertProducts: [],
            productsSubscriber: null,
        };
    },
    props: {
        merchantId: {
            type: Number,
            default: null,
            required: true,
        },
        merchantTotal: {
            type: Number,
            default: null,
            required: true,
        },
        cols: {
            default: 1,
            type: Number,
        },
    },
    created() {
        const basketItems = [];
        for (const basketItem of Object.values(this.$basket.getBasketItems())) {
            basketItems.push(basketItem.catalog_item_id);
        }

        this.productsSubscriber = getProducts(
            new Observable(subscriber => {
                this.$watch(
                    'freeShippingThreshold',
                    val => {
                        if (val > 0) {
                            const crosssells = [];
                            const categories = [];
                            for (const product of Object.values(this.products)) {
                                product.categories.forEach(category => categories.push(category));
                                product.crosssells.forEach(crosssell => crosssells.push(crosssell));
                            }

                            const limit = 5;
                            let categoryProductsLimit = 0;

                            const uniqueCorsssells = crosssells
                                .filter(crosssell => !basketItems.includes(crosssell))
                                .slice(0, limit);

                            if (uniqueCorsssells.length || categories.length) {
                                if (uniqueCorsssells.length < limit) {
                                    categoryProductsLimit = limit - uniqueCorsssells.length;
                                }
                                subscriber.next({
                                    ids: uniqueCorsssells,
                                    categories: categoryProductsLimit ? [...new Set(categories)] : [],
                                    categoryProductsLimit: categoryProductsLimit,
                                    unacceptableProducts: basketItems,
                                });
                            }
                        }
                    },
                    {
                        immediate: true,
                    },
                );
            }),
        ).subscribe(result => {
            const products = [];
            if (result && result.length) {
                result.some(product => {
                    if (!product.variantsIds.length) {
                        if (!basketItems.includes(product.id)) {
                            products.push(product);
                        }
                    }
                    return products.length === 5;
                });
                this.offertProducts = products;
            }
        });
    },
    beforeDestroy() {
        this.productsSubscriber.unsubscribe();
    },
    computed: {
        customerCountryId() {
            let countryId = null;
            if (Vue.$store.state.CheckoutAddressStep) {
                let countryName = null;
                if (Vue.$store.state.CheckoutAddressStep.otherAddress) {
                    countryName = Vue.$store.state.ShippingAddressForm.address.country;
                } else {
                    countryName = Vue.$store.state.BillingAddressForm.address.country;
                }
                Object.values(this.$country.getCountries()).some(country => {
                    if (country.name === countryName) {
                        countryId = country.id;
                    }
                    return countryId;
                });
            } else {
                countryId = Vue.$country.getCountryID();
            }
            return countryId ? countryId : Vue.$country.getCountryID();
        },
        freeShippingThreshold() {
            let freeShippingThreshold = 0;
            const customerCountryId = this.customerCountryId;
            if (customerCountryId && this.shippingPrices[this.merchantId]) {
                this.shippingPrices[this.merchantId].some(shippingPrice => {
                    if (
                        shippingPrice.country_id == customerCountryId &&
                        shippingPrice.value === 0 &&
                        shippingPrice.minimum > this.merchantTotal
                    ) {
                        freeShippingThreshold = shippingPrice.minimum - this.merchantTotal;
                    }
                });
            }
            return freeShippingThreshold;
        },
        ...mapState('Product', ['shippingPrices']),
        ...mapState('Basket', ['products']),
    },
    methods: {
        getFreeShippingMinimalPrice(freeShippingThreshold) {
            return freeShippingThreshold + this.merchantTotal;
        },
        ...mapActions('Product', ['getShippingPrices']),
    },
    watch: {
        customerCountryId: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.getShippingPrices({
                        merchant: parseInt(this.merchantId),
                        country: val,
                    });
                }
            },
        },
    },
};
</script>
